import { cutToast } from 'utility/toast';
import { makeVar, useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CorporateWorkgroupInput,
  CorporateWorkgroupPatch,
  EntityDeleteInput,
  WorkgroupCurrencyInput,
  WorkgroupCurrencyUpdateTypeInput,
  WorkgroupUserInput,
} from 'common/types/globalTypes';
import { differenceBy, isEmpty } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import {
  CorporateWorkgroup,
  CorporateWorkgroupVariables,
} from './__generated__/CorporateWorkgroup';
import {
  CorporateWorkgroupCreate,
  CorporateWorkgroupCreateVariables,
} from './__generated__/CorporateWorkgroupCreate';
import {
  CorporateWorkgroupUpdate,
  CorporateWorkgroupUpdateVariables,
} from './__generated__/CorporateWorkgroupUpdate';
import { CORPORATE_WORKGROUP_VALUES } from './constants';
import { FormView } from './FormView';
import { CorporateWorkgroupValues, workgroupUserType } from './types';
import {
  getCreatedWorkgroupUsers,
  getCurrencypatch,
  getDefaultValues,
  getDeletedWorkgroupUsers,
} from './utils';
import { validationSchema } from './validations';
import CORPORATE_WORKGROUP from './CorporateWorkgroup.graphql';
import CORPORATE_WORKGROUP_CREATE from './CorporateWorkgroupCreate.graphql';
import CORPORATE_WORKGROUP_UPDATE from './CorporateWorkgroupUpdate.graphql';
import CORPORATE_WORKGROUP_FIELDS from '../CorporateWorkgroupsFields.graphql';
const CORPORATE_WORKGROUP_FRAGMENT_NAME = 'CorporateWorkgroupsFields';
export const isPOCreated = makeVar<boolean>(false);
export const CorporateWorkGroupView = () => {
  const history = useHistory();
  const { workGroupId } = useParams<{ workGroupId: string | undefined }>();
  const saveAnotherTransaction = useRef<boolean>(false);
  const isNew = !workGroupId;

  const {
    data: corporateWorkgroupData,
    loading: corporateWorkgroupDataLoading,
    refetch,
  } = useQuery<CorporateWorkgroup, CorporateWorkgroupVariables>(
    CORPORATE_WORKGROUP,
    {
      variables: {
        id: workGroupId!,
      },
      skip: !workGroupId,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const [
    createCorporateWorkgroup,
    { loading: createCorporateWorkgroupLoading },
  ] = useMutation<CorporateWorkgroupCreate, CorporateWorkgroupCreateVariables>(
    CORPORATE_WORKGROUP_CREATE,
    { errorPolicy: 'all' }
  );

  const [
    updateCorporateWorkgroup,
    { loading: updateCorporateWorkgroupLoading },
  ] = useMutation<CorporateWorkgroupUpdate, CorporateWorkgroupUpdateVariables>(
    CORPORATE_WORKGROUP_UPDATE,
    { errorPolicy: 'all' }
  );

  const formMethods = useForm<CorporateWorkgroupValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const { handleSubmit, trigger, reset } = { ...formMethods };

  const { _rowTimestamp } = { ...corporateWorkgroupData?.corporateWorkgroup };

  const onHandleSubmit = async (values: CorporateWorkgroupValues) => {
    const { workgroupUsers, workgroupCurrencies, ...rest } = { ...values };
    const requestPayLoad: CorporateWorkgroupInput = {
      ...rest,
      workgroupNumber: values.workgroupNumber || '',
      name: values.name || '',
      prefixCode: values.prefixCode || '',
      productionPhaseId: values.productionPhaseId!,
      chartOfAccountsId: values.chartOfAccountsId || '',
      businessUnitId: values.businessUnitId || '',
      productionStatusTypeId: values.productionStatusTypeId!,
    };
    if (isNew) {
      const userProfileIdObjects: (WorkgroupUserInput | null)[] | null =
        values?.workgroupUsers
          ?.map((item) => {
            if (item.userProfileId !== null) {
              return { userProfileId: item.userProfileId };
            }
            return null; // Return null if userProfileId is null
          })
          .filter((item): item is WorkgroupUserInput => item !== null) || null;

      const workgroupCurrencies =
        values?.workgroupCurrencies &&
        (values?.workgroupCurrencies
          ?.filter((curr) => !curr.id && curr.currencyId !== null)
          .map(
            ({
              id,
              _rowTimestamp,
              name,
              _isActive,
              _isUpdatable,
              _isDeletable,
              ...curr
            }) => curr
          ) as WorkgroupCurrencyInput[]);

      const { errors, data } = await createCorporateWorkgroup({
        variables: {
          input: {
            corporateWorkgroup: requestPayLoad,
            workgroupUsers: userProfileIdObjects,
            workgroupCurrencies: workgroupCurrencies,
          },
        },

        update: (cache, { data }) => {
          if (data?.corporateWorkgroupCreate?.corporateWorkgroup) {
            cache.modify({
              fields: {
                corporateWorkgroups(existing) {
                  if (data?.corporateWorkgroupCreate?.corporateWorkgroup) {
                    const newWorkGroupRef = cache.writeFragment({
                      data: data.corporateWorkgroupCreate.corporateWorkgroup,
                      fragment: CORPORATE_WORKGROUP_FIELDS,
                      fragmentName: CORPORATE_WORKGROUP_FRAGMENT_NAME,
                    });
                    return {
                      ...existing,
                      nodes: [newWorkGroupRef, ...existing.nodes],
                    };
                  }
                },
              },
            });
          }
        },
      });
      if (errors?.length) {
        cutToast.error(errors[0].message);
      }
      if (data?.corporateWorkgroupCreate?.corporateWorkgroup) {
        if (!saveAnotherTransaction.current) {
          history.replace(
            `/account-management/work-groups/work-group/${data?.corporateWorkgroupCreate?.corporateWorkgroup.id}`
          );
          isPOCreated(true);
        } else {
          reset(CORPORATE_WORKGROUP_VALUES);
          trigger();
          isPOCreated(false);
        }
        cutToast.success('Workgroup added successfully.');
      }
    } else {
      const defaultValues = getDefaultValues({
        isNew,
        corporateWorkgroupData,
      });
      const corporateWorkgroupPatch: CorporateWorkgroupPatch = Object.entries(
        requestPayLoad
      ).reduce((res, [key, val]) => {
        if (val !== defaultValues[key as keyof CorporateWorkgroupValues]) {
          return { ...res, [key]: val };
        }
        return res;
      }, {});

      const workgroupUserCreatedList = getCreatedWorkgroupUsers(
        defaultValues.workgroupUsers as workgroupUserType[],
        values.workgroupUsers as workgroupUserType[]
      );
      const workgroupUserDeletedList = getDeletedWorkgroupUsers(
        defaultValues.workgroupUsers as workgroupUserType[],
        values.workgroupUsers as workgroupUserType[]
      );

      const workgroupCurrencyCreate = workgroupCurrencies
        ? workgroupCurrencies
            .filter((item) => !item.id && item.currencyId !== null)
            .map(
              ({
                id,
                _rowTimestamp,
                _isUpdatable,
                name,
                _isActive,
                _isDeletable,
                ...item
              }) => item
            )
        : ([] as WorkgroupCurrencyInput[]);

      const workgroupCurrencyUpdate: WorkgroupCurrencyUpdateTypeInput[] =
        getCurrencypatch(
          defaultValues.workgroupCurrencies ?? [],
          workgroupCurrencies ?? []
        );
      const workgroupCurrencyDelete =
        defaultValues.workgroupCurrencies &&
        (differenceBy(
          defaultValues.workgroupCurrencies,
          workgroupCurrencies || [],
          'id'
        )
          .filter((item) => !!item.id && !!item._rowTimestamp)
          .map((ele) => ({
            id: ele.id,
            rowTimestamp: ele._rowTimestamp,
          })) as EntityDeleteInput[]);

      if (!!workGroupId && !!_rowTimestamp) {
        const { errors } = await updateCorporateWorkgroup({
          variables: {
            input: {
              id: workGroupId,
              rowTimestamp: _rowTimestamp,
              corporateWorkgroupPatch: !isEmpty(corporateWorkgroupPatch)
                ? corporateWorkgroupPatch
                : undefined,
              workgroupUserCreate: workgroupUserCreatedList ?? [],
              workgroupUserDelete: workgroupUserDeletedList ?? [],
              workgroupCurrencyCreate: workgroupCurrencyCreate,
              workgroupCurrencyUpdate: workgroupCurrencyUpdate,
              workgroupCurrencyDelete: workgroupCurrencyDelete,
            },
          },
        });
        if (errors?.length) {
          cutToast.error(errors[0].message);
        } else {
          refetch();
          cutToast.success('Workgroup edited successfully.');
        }
      }
    }
  };

  useEffect(() => {
    const defaultValues = getDefaultValues({
      isNew,
      corporateWorkgroupData,
    });
    reset(defaultValues);
    trigger();
  }, [corporateWorkgroupData, isNew, reset, trigger]);

  return (
    <FormProvider {...formMethods}>
      <FormView
        isNew={isNew}
        corporateWorkgroupData={corporateWorkgroupData}
        dataLoading={corporateWorkgroupDataLoading}
        isCreating={createCorporateWorkgroupLoading}
        isUpdating={updateCorporateWorkgroupLoading}
        onSave={handleSubmit(onHandleSubmit)}
        isSaveAnother={(value) => (saveAnotherTransaction.current = value)}
        refetch={refetch}
      />
    </FormProvider>
  );
};
